import { Injectable } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { MessageService } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';

@Injectable()
export class HttpErrorInterceptor implements HttpInterceptor {
  private readonly ERROR_TOAST_KEY = 'http-interceptor-error';
  private errorCount = 0;
  private resetTimer: any;
  private updateTimer: any;
  private lastErrorMessage = '';

  constructor(
    private messageService: MessageService,
    private translateService: TranslateService,
  ) {}

  intercept = (request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> => {
    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        // Les 401 (session invalide/expirée) sont gérés par le RequestInterceptor, qui
        // déconnecte et redirige vers /login. On n'affiche pas de toast d'erreur dans ce cas
        // pour ne pas faire clignoter un message déroutant pendant la redirection.
        if (error.status === 401) {
          return throwError(() => error);
        }

        // Stocker le nouveau message d'erreur et le tronquer si trop long
        let errorMessage = error.error?.message ?? error.message;
        const maxLength = 300;
        if (errorMessage.length > maxLength) {
          errorMessage = errorMessage.substring(0, maxLength) + '...';
        }
        this.lastErrorMessage = errorMessage;

        // Incrémenter le compteur d'erreurs
        this.errorCount++;

        // Annuler les timers existants
        if (this.resetTimer) {
          clearTimeout(this.resetTimer);
        }
        if (this.updateTimer) {
          clearTimeout(this.updateTimer);
        }

        // Mettre à jour le toast après un court délai pour regrouper les erreurs rapides
        this.updateTimer = setTimeout(() => {
          const baseTitle = this.translateService.instant('common.messages.errorOccurred');
          const summary = this.errorCount > 1 ? `${baseTitle} (${this.errorCount})` : baseTitle;

          // Effacer et recréer le toast
          this.messageService.clear(this.ERROR_TOAST_KEY);

          // Petit délai pour laisser l'animation de fermeture se terminer
          setTimeout(() => {
            this.messageService.add({
              key: this.ERROR_TOAST_KEY,
              severity: 'error',
              summary: summary,
              detail: this.lastErrorMessage,
              life: 5000,
              closable: true,
              sticky: false,
            });
          }, 100);

          // Réinitialiser le compteur après la fermeture du toast
          this.resetTimer = setTimeout(() => {
            this.errorCount = 0;
            this.lastErrorMessage = '';
          }, 5100);
        }, 150);

        return throwError(error);
      }),
    );
  };
}
